import { FC } from 'react'

import { MenuItem, Select, Stack, Typography } from '@mui/material'
import { FifaCircleFlag } from '@/components/CircleFlag'
import type { Standing } from '@/types/dde/golfLeaderboard.types'

import { GOLF_SCORECARD_HEADER_TEST_IDS } from './constants'
import { GolfScoreboardCells } from './GolfScoreboardCells'
import {
  CountryFlag,
  DisplayName,
  GolfScorecardContainer,
  HeaderContent,
  IdTeamInfo,
  PlayerInfo
} from './styles'

export type TeamRoundHeaderProps = {
  roundNo: number
  teamNo: number
  strokeNo: number
  playerId: number
  holeNo: number
  ballLie: {
    distanceToPin: number
    surfaceType: string
  }
  player: {
    displayName: string
    country: string
  }
}

interface GolfScorecardHeaderProps {
  teamRound: TeamRoundHeaderProps
  tournamentId: string
  teamStanding?: Standing
  availableRounds?: number[]
  hasPlayoff?: boolean
  selectedRound?: number | string
  onRoundChange?: (round: number | string) => void
}

export const GolfScorecardHeader: FC<GolfScorecardHeaderProps> = ({
  teamRound,
  teamStanding,
  availableRounds = [],
  hasPlayoff = false,
  selectedRound = 1,
  onRoundChange
}) => {
  const {
    teamNo,
    playerId,
    player: { displayName, country }
  } = teamRound

  return (
    <GolfScorecardContainer data-testid={GOLF_SCORECARD_HEADER_TEST_IDS.header}>
      <HeaderContent>
        <PlayerInfo>
          <CountryFlag>
            {country ? <FifaCircleFlag fifaCode={country} height={20} /> : null}
          </CountryFlag>
          <DisplayName data-testid={GOLF_SCORECARD_HEADER_TEST_IDS.displayName}>
            {displayName || '-'}
          </DisplayName>
        </PlayerInfo>
        <IdTeamInfo>
          <Typography variant="caption" data-testid={GOLF_SCORECARD_HEADER_TEST_IDS.playerId}>
            Player ID: {playerId || '-'}
          </Typography>
          <Typography variant="caption" data-testid={GOLF_SCORECARD_HEADER_TEST_IDS.teamNo}>
            Team No. {teamNo || '-'}
          </Typography>
        </IdTeamInfo>
      </HeaderContent>
      <Stack
        direction="row"
        gap={2}
        alignItems="center"
        sx={{
          width: { xs: '100%', sm: 'auto' },
          justifyContent: { xs: 'space-between', sm: 'flex-end' },
          ml: { xs: 0, sm: 'auto' },
          mt: { xs: 1, sm: 0 }
        }}
      >
        <GolfScoreboardCells teamRound={teamRound} teamStanding={teamStanding} />
        {(availableRounds.length > 0 || hasPlayoff) && (
          <Select
            value={selectedRound}
            onChange={(e) => onRoundChange?.(e.target.value)}
            size="small"
            sx={{ minWidth: { xs: 100, sm: 120 }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
          >
            {availableRounds.map((round) => (
              <MenuItem key={round} value={round}>
                Round {round}
              </MenuItem>
            ))}
            {hasPlayoff && <MenuItem value="401">Playoff</MenuItem>}
          </Select>
        )}
      </Stack>
    </GolfScorecardContainer>
  )
}
